import { format, isValid, parseISO } from 'date-fns';
import {
  API_DATE_FORMAT,
  BULK_IMPORT_COLUMNS,
  BULK_IMPORT_TEMPLATE_FILE_NAME,
  BULK_IMPORT_TEMPLATE_ROWS,
  DISPLAY_DATE_FORMAT,
  DISPLAY_DATE_TIME_FORMAT,
  Warehouse,
} from './debtTrackingConstants';

export const formatMoney = (amount: number, showPlusSign = false) => {
  const value = Number(amount) || 0;
  const formatted = Math.abs(value).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (value < 0) return `-$${formatted}`;
  if (showPlusSign && value > 0) return `+$${formatted}`;
  return `$${formatted}`;
};

const toDate = (value: string | Date) => (value instanceof Date ? value : parseISO(value));

export const formatDisplayDate = (value: string) =>
  value && isValid(toDate(value)) ? format(toDate(value), DISPLAY_DATE_FORMAT) : '';

export const formatDisplayDateTime = (value: string) =>
  value && isValid(toDate(value)) ? format(toDate(value), DISPLAY_DATE_TIME_FORMAT) : '';

export const toApiDate = (value: Date) => (value && isValid(value) ? format(value, API_DATE_FORMAT) : '');

export const fromApiDate = (value: string) => (value ? parseISO(value) : null);

export const todayApiDate = () => toApiDate(new Date());

export const formatWarehouseLabel = (warehouse?: Warehouse) =>
  warehouse ? `${warehouse.name} · ${warehouse.level} · ${warehouse.provider}` : '';

export const pluralize = (count: number, singular: string, plural = `${singular}s`) =>
  `${count} ${count === 1 ? singular : plural}`;

// Accepts loan IDs separated by commas, spaces or new lines: "85290, 85215\n84901"
export const parseLoanIds = (value: string) =>
  Array.from(new Set(value.split(/[\s,;]+/).map(id => id.trim()).filter(Boolean)));

export const isNumericLoanId = (loanId: string) => /^\d+$/.test(loanId);

// A financing amount is a balance that replaces the previous one: zero (fully repaid) is fine, negative isn't.
export const isValidAmount = (value: number | string) =>
  value !== '' && value !== null && value !== undefined && !isNaN(Number(value)) && Number(value) >= 0;

// Service rejects with ApiError({ response }); roc-services error bodies are { status: 'ERROR', error: { message } }.
export const getApiErrorMessage = (error: any, fallback: string) =>
  error?.error?.response?.data?.error?.message ?? fallback;

const toCsvCell = (value: string) => (/[",\n]/.test(value) ? `"${value.replace(/"/g, '""')}"` : value);

export const downloadBulkImportTemplate = () => {
  const csv = [BULK_IMPORT_COLUMNS.map(column => column.label), ...BULK_IMPORT_TEMPLATE_ROWS]
    .map(row => row.map(toCsvCell).join(','))
    .join('\n');
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }));
  const link = document.createElement('a');
  link.href = url;
  link.download = BULK_IMPORT_TEMPLATE_FILE_NAME;
  link.click();
  URL.revokeObjectURL(url);
};
